import React, { useState, useRef, useEffect, useCallback } from 'react';
import './LetterForm.css';
import logoImg from '../images/V2_DVF_Logo_Transparent_web.png';
import stampImg   from '../images/WWII Post Stamp.png';
import soundKey   from '../sounds/typewriter-key.wav';
import soundSpace from '../sounds/typewriter-space.wav';

// ─── Typewriter sounds ───────────────────────────────────────────────────────
// Web Audio: each file is decoded once, then every keystroke plays from memory
// with near-zero latency (HTML <audio> seeks and buffers on every play).
// The recordings have silence before the strike, so playback starts just
// before it: the key strike lands at ~158ms, the space bar at ~84ms.
const SOUND_START = { key: 0.150, space: 0.080 };
const SOUND_VOLUME = 0.6;

let audioCtx = null, soundGain = null;
const soundBuffers = {};

// Call from a user gesture (browsers only start audio after one)
function initSounds() {
  if (audioCtx) {
    if (audioCtx.state === 'suspended') audioCtx.resume();
    return;
  }
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  audioCtx  = new Ctx();
  soundGain = audioCtx.createGain();
  soundGain.gain.value = SOUND_VOLUME;
  soundGain.connect(audioCtx.destination);
  [['key', soundKey], ['space', soundSpace]].forEach(([name, src]) => {
    fetch(src)
      .then(res => res.arrayBuffer())
      // Callback form of decodeAudioData for older Safari
      .then(data => new Promise((resolve, reject) => audioCtx.decodeAudioData(data, resolve, reject)))
      .then(buf => { soundBuffers[name] = buf; })
      .catch(() => {});
  });
}

function playSoundBuffer(name) {
  const buf = soundBuffers[name];
  if (!audioCtx || !buf) return;
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  src.connect(soundGain);
  src.start(0, SOUND_START[name]);
}

function formatDate() {
  return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// Approximate lines per page (used only to trigger new page sheet)
const LINES_PER_PAGE = 20;
const MAX_MSG_CHARS  = 5000; // 20 lines fits the visible paper
const SEND_TIMEOUT_MS = 15000; // give up on a stalled request and show the fallback
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LetterForm() {
  const [stage, setStage]             = useState('idle');
  const [name, setName]               = useState('');
  const [email, setEmail]             = useState('');
  // Each page is a string; pages grow like Google Docs sheets
  const [pages, setPages]             = useState(['']);
  const [currentPage, setCurrentPage] = useState(0);
  const [submitting, setSub]          = useState(false);
  const [error, setError]             = useState('');
  const [soundOn, setSoundOn]         = useState(true);
  const [arcDown, setArcDown]         = useState(false);
  const [sendSuccess, setSendSuccess] = useState(null); // null | true | false
  const [cooldown,    setCooldown]    = useState(false);

  const timers      = useRef([]);
  const textareas   = useRef([]);
  // Track last key so paste (no keydown char) only plays once via onChange
  const lastKey     = useRef(null);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const schedule = useCallback((pairs) => {
    clearTimers();
    pairs.forEach(([ms, s]) => timers.current.push(setTimeout(() => setStage(s), ms)));
  }, []);
  useEffect(() => () => clearTimers(), []);

  // ── Derived flags ─────────────────────────────────────────────────────────
  const isIdle       = stage === 'idle';
  const isFlipping   = stage === 'flipping';
  const isAddressing = stage === 'addressing';
  const isOpening    = stage === 'opening';
  const isLetterOut  = stage === 'letter-out';
  const isWriting    = stage === 'writing';
  const isFolding    = stage === 'folding';
  const isSealing    = stage === 'sealing';
  const isFlipToBack = stage === 'flip-to-back';
  const isStamping   = stage === 'stamping';
  const isSending    = stage === 'sending';
  const isSent       = stage === 'sent';

  const showBack   = isFlipping || isAddressing;
  const showLetter = isLetterOut || isWriting || isFolding;
  const backFace   = showBack || isFlipToBack || isStamping || isSending || isSent;
  const showStamp  = isStamping || isSending || isSent;
  const behindEnv  = isLetterOut || isWriting;

  // ── Sound helper ─────────────────────────────────────────────────────────
  const playSound = useCallback((type) => {
    if (!soundOn) return;
    initSounds();
    playSoundBuffer(type);
  }, [soundOn]);

  // ── Step 1: click idle envelope ───────────────────────────────────────────
  const handleEnvelopeClick = () => {
    if (stage !== 'idle' && stage !== 'writing') return;
    // Start loading sounds now so they're decoded before the first keystroke
    initSounds();
    schedule([[0, 'flipping'], [700, 'addressing']]);
  };

  // ── Step 2: submit address ────────────────────────────────────────────────
  const handleAddressSubmit = (e) => {
    e.stopPropagation();
    if (!name.trim() || !email.trim()) { setError('Please fill in your name and email.'); return; }
    if (!EMAIL_RE.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    setError('');
    schedule([[0, 'opening'], [800, 'letter-out'], [1600, 'writing']]);
    timers.current.push(setTimeout(() => textareas.current[0]?.focus(), 1800));
  };

  // ── Envelope input sounds ────────────────────────────────────────────────
  const handleEnvKey = useCallback((e) => {
    if (e.key === ' ' || e.key === 'Backspace' || e.key === 'Delete') playSound('space');
    else if (e.key.length === 1) playSound('key');
  }, [playSound]);

  // ── Textarea keydown: track what was pressed for onChange ─────────────────
  const handleKeyDown = useCallback((e) => {
    // Detect paste vs typing the letter 'v'
    lastKey.current = (e.key === 'v' && (e.ctrlKey || e.metaKey)) ? 'paste' : e.key;
    if (e.key === ' ' || e.key === 'Backspace' || e.key === 'Delete') {
      playSound('space');
    } else if (e.key.length === 1) {
      playSound('key');
    }
  }, [playSound]);

  // ── Text change: handle page overflow (Google Docs style) ────────────────
  const handleChange = useCallback((e, pageIdx) => {
    const val   = e.target.value;
    const lines = val.split('\n');

    // Play single sound for paste
    if (lastKey.current === null || lastKey.current === 'paste') {
      playSound('key');
    }
    lastKey.current = null;

    if (lines.length > LINES_PER_PAGE) {
      // Overflow to next page
      const thisPage = lines.slice(0, LINES_PER_PAGE).join('\n');
      const overflow = lines.slice(LINES_PER_PAGE).join('\n');
      setPages(prev => {
        const next    = [...prev];
        next[pageIdx] = thisPage;
        next[pageIdx+1] = overflow + (next[pageIdx+1] || '');
        return next;
      });
      setCurrentPage(pageIdx + 1);
      setTimeout(() => {
        const ta = textareas.current[pageIdx + 1];
        if (ta) { ta.focus(); ta.setSelectionRange(overflow.length, overflow.length); }
      }, 50);
    } else {
      setPages(prev => { const n = [...prev]; n[pageIdx] = val; return n; });
    }
  }, [playSound]);

  // ── Arrow navigation between pages ──────────────────────────────────────
  const goToPage = useCallback((idx) => {
    setCurrentPage(idx);
    setTimeout(() => textareas.current[idx]?.focus(), 50);
  }, []);

  // ── Step 3: send ──────────────────────────────────────────────────────────
  const handleSend = async (e) => {
    e.stopPropagation();
    if (cooldown) { setError('Please wait a moment before sending again.'); return; }
    const msg = pages.join('\n\n');
    if (!msg.trim()) { setError('Your letter appears to be blank.'); return; }
    if (msg.length > MAX_MSG_CHARS) {
      setError(`Message too long (${msg.length}/${MAX_MSG_CHARS} chars). Please shorten it.`);
      return;
    }
    setError(''); setSub(true); setSendSuccess(null);

    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), SEND_TIMEOUT_MS);
    try {
      const fd = new FormData();
      fd.append('access_key', '3cfd10d6-e454-4dc8-abe9-72a868709cb5');
      fd.append('name', name.trim());
      fd.append('email', email.trim());
      fd.append('message', msg);
      fd.append('subject', `Portfolio message from ${name.trim()}`);
      fd.append('botcheck', '');
      const res  = await fetch('https://api.web3forms.com/submit', { method:'POST', body:fd, signal:controller.signal });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Submission failed');
    } catch(err) {
      console.error('Web3Forms:', err);
      setError('Message failed to send. Please email me directly at dmitriyfilimonov33@gmail.com');
      setSub(false);
      setSendSuccess(false);
      return;
    } finally {
      clearTimeout(abortTimer);
    }

    setSub(false);

    // Apply send cooldown (30 seconds) to prevent spam
    setCooldown(true);
    setTimeout(() => setCooldown(false), 30000);

    schedule([
      [0,    'folding'],
      [900,  'sealing'],
      [1400, 'flip-to-back'],
      [1900, 'stamping'],
      [2400, 'sending'],
      [3400, 'sent'],
      [4000, 'idle'],
    ]);
    timers.current.push(setTimeout(() => setArcDown(true), 840));
    timers.current.push(setTimeout(() => {
      setName(''); setEmail(''); setPages(['']); setCurrentPage(0);
      setArcDown(false);
    }, 6200));
    // Show success message AFTER animation completes (4s), keep for 4s, then fade out
    timers.current.push(setTimeout(() => setSendSuccess(true), 4000));
    timers.current.push(setTimeout(() => setSendSuccess(null), 8000));
  };

  // ── Envelope class ────────────────────────────────────────────────────────
  const envClass = [
    'lf-env',
    backFace && !isFlipToBack ? 'lf-env--back'        : '',
    isFlipToBack              ? 'lf-env--flip-to-back' : '',
    isOpening                 ? 'lf-env--opening'      : '',
    behindEnv                 ? 'lf-env--behind'       : '',
    isFolding || isSealing || isFlipToBack || isStamping ? 'lf-env--grow' : '',
    isSending                 ? 'lf-env--sending'      : '',
    isSent                    ? 'lf-env--sent'         : '',
  ].filter(Boolean).join(' ');

  // ── Page stack ────────────────────────────────────────────────────────────
  const renderPages = () => {
    const total = pages.length;
    const sB    = Math.min(currentPage, 2);
    const sA    = Math.min(total-1-currentPage, 2);

    return (
      <div className="lf-stack">
        {/* Pages behind (previous) -- peeking above */}
        {Array.from({length:sB}).map((_,i) => {
          const off = sB-i;
          return (
            <div key={`b${i}`}
              className={`lf-page lf-page--behind lf-page--behind-${off}`}>
              <div className="lf-page__paper">
                <pre className="lf-page__preview">{pages[currentPage-off]}</pre>
              </div>
            </div>
          );
        })}

        {/* Current page */}
        <div className="lf-page lf-page--current">
          <div className="lf-page__paper">
            {/* Fold overlay appears during folding animation */}
            {isFolding && <div className="lf-page__fold-overlay" />}

            {/* Logo top-left of letter paper */}
            {currentPage === 0 && (
              <img src={logoImg} alt="DVF" className="lf-page__logo" />
            )}

            {currentPage === 0 && (
              <div className="lf-page__address-block">
                <p>{name||'___'}</p>
                <p>{email||'___'}</p>
                <p>{formatDate()}</p>
              </div>
            )}

            <textarea
              ref={el => textareas.current[currentPage] = el}
              className="lf-page__textarea"
              value={pages[currentPage]}
              onChange={e => handleChange(e, currentPage)}
              onKeyDown={handleKeyDown}
              disabled={!isWriting||submitting}
              placeholder="Begin your message here..."
              spellCheck={false}
            />

            {/* Page navigation -- arrows + count */}
            {total > 1 && (
              <div className="lf-page__nav">
                <button
                  className="lf-page__nav-btn"
                  onClick={e => { e.stopPropagation(); goToPage(currentPage-1); }}
                  disabled={currentPage === 0}
                  title="Previous page"
                >←</button>
                <span className="lf-page__num">{currentPage+1} / {total}</span>
                <button
                  className="lf-page__nav-btn"
                  onClick={e => { e.stopPropagation(); goToPage(currentPage+1); }}
                  disabled={currentPage === total-1}
                  title="Next page"
                >→</button>
              </div>
            )}
          </div>
        </div>

        {/* Pages ahead (next) -- peeking below */}
        {Array.from({length:sA}).map((_,i) => {
          const off = i+1;
          return (
            <div key={`a${i}`}
              className={`lf-page lf-page--ahead lf-page--ahead-${off}`}>
              <div className="lf-page__paper"/>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="lf-stage">

      {/* Sound toggle */}
      <button
        className={`lf-sound-toggle ${soundOn?'lf-sound-toggle--on':'lf-sound-toggle--off'}`}
        onClick={() => setSoundOn(s => !s)}
        title={soundOn ? 'Mute typewriter sounds' : 'Enable typewriter sounds'}
      >
        <span className="lf-sound-toggle__icon" aria-hidden="true">T</span>
        <span className="lf-sound-toggle__label">{soundOn ? 'Sound On' : 'Sound Off'}</span>
        {!soundOn && <span className="lf-sound-toggle__strike" />}
      </button>

      {/* Envelope */}
      <div
        className={envClass}
        onClick={isIdle||isWriting ? handleEnvelopeClick : undefined}
        role="button" tabIndex={0}
        onKeyDown={e => e.key==='Enter'&&(isIdle||isWriting)&&handleEnvelopeClick()}
      >
        {/* FRONT */}
        <div className="lf-env__front">
          <div className="lf-env__flap" />
          <div className="lf-env__fold lf-env__fold--left" />
          <div className="lf-env__fold lf-env__fold--right" />
          <div className="lf-env__fold lf-env__fold--bottom" />
          {isIdle && <div className="lf-env__hint">Click to write a message <span className="kutz-icon" aria-hidden="true">V</span></div>}
        </div>

        {/* BACK -- plain, no flap */}
        <div className="lf-env__back">
          <div className="lf-env__back-inner">
            {showStamp && (
              <div className="lf-stamp">
                <img src={stampImg} alt="WWII Airmail stamp" className="lf-stamp__img" />
              </div>
            )}
            {(isAddressing||isFlipping) ? (
              <>
                <p className="lf-env__back-label">Your Details</p>
                <div className="lf-fields">
                  <div className="lf-field">
                    {!name  && <span className="lf-field__ghost">Your Name</span>}
                    <input className="lf-field__input" type="text" value={name}
                      onChange={e => setName(e.target.value)} onKeyDown={handleEnvKey}
                      disabled={!isAddressing} maxLength={60} autoComplete="name"
                      onClick={e => e.stopPropagation()} />
                  </div>
                  <div className="lf-field">
                    {!email && <span className="lf-field__ghost">your@email.com</span>}
                    <input className="lf-field__input" type="email" value={email}
                      onChange={e => setEmail(e.target.value)} onKeyDown={handleEnvKey}
                      disabled={!isAddressing} maxLength={80} autoComplete="email"
                      onClick={e => e.stopPropagation()} />
                  </div>
                </div>
                {error && <p className="lf-error">{error}</p>}
                {isAddressing && (
                  <button className="lf-indicator" onClick={handleAddressSubmit}>→</button>
                )}
              </>
            ) : (name||email) && (
              <div className="lf-env__sender">
                {name  && <p>{name}</p>}
                {email && <p>{email}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Letter */}
      {showLetter && (
        <div className={[
          'lf-letter',
          isLetterOut            ? 'lf-letter--emerging' : '',
          isWriting              ? 'lf-letter--open'     : '',
          isFolding              ? 'lf-letter--folding'  : '',
          isFolding && arcDown   ? 'lf-letter--arc-down' : '',
        ].filter(Boolean).join(' ')}>
          {renderPages()}
          {error && isWriting && <p className="lf-error lf-error--letter">{error}</p>}
          {isWriting && (
            <button className="lf-indicator lf-indicator--send"
              onClick={handleSend} disabled={submitting || cooldown}
              title={cooldown ? 'Please wait before sending again' : 'Send letter'}>
              {submitting ? '⏳' : '→'}
            </button>
          )}
        </div>
      )}

      {sendSuccess === true && (
        <div className="lf-sent-msg lf-sent-msg--success">
          <span className="lf-sent-msg__icon">✓</span>
          Your letter is on its way. I'll be in touch soon.
        </div>
      )}
      {sendSuccess === false && (
        <div className="lf-sent-msg lf-sent-msg--failure">
          <span className="lf-sent-msg__icon">✕</span>
          Message failed to send.{' '}
          <a href="mailto:dmitriyfilimonov33@gmail.com">
            Email me directly
          </a>{' '}
          instead.
        </div>
      )}
    </div>
  );
}